/** @jsxRuntime automatic */
import { useCallback, useLayoutEffect, useRef, type ReactNode } from "react";
import { claimHeldFocus } from "./focus-hold";
import { announce, announceableText, useAnnounceRef } from "./live-region";
import { getFocusableElements } from "./overlay-utils";
import { cn } from "./utils";

/**
 * One visual language for empty states (spec 07 › Estados vacíos,
 * visual-system-13, states-motion-18): a page or a section with nothing yet
 * always carries a drawing of the set (`illustration`, required by type), and
 * only a compact «no results» inside a list or a panel may use an icon tile.
 */
export type EmptyStateVisual =
  | {
      /** Page or section empty: a spot illustration of `@winche/ui` (96 px; 72 on a phone). */
      illustration: ReactNode;
      icon?: never;
      size?: "default";
    }
  | {
      /** 20–24 px icon in a 40 px muted tile, or a small illustration. */
      icon?: ReactNode;
      illustration?: ReactNode;
      /** `compact` for "no results" inside lists and panels. */
      size: "compact";
    };

export type EmptyStateProps = EmptyStateVisual & {
  title: ReactNode;
  /** One or two lines, max 44ch. */
  description?: ReactNode;
  /** What is still available after this empty result (never implies data loss). */
  preserved?: ReactNode;
  /** Primary action (Button). */
  action?: ReactNode;
  /** Secondary action (Button variant="ghost"). */
  secondaryAction?: ReactNode;
  /**
   * `plain` (default): open area, no border — first-run and empty pages.
   * `card`: inside a bordered surface. `dashed`: legacy drop/placeholder box.
   */
  variant?: "plain" | "card" | "dashed";
  /**
   * The person just emptied it (the inbox at zero, a batch with nothing
   * left, every step done): it rises in (`enter-rise`, 200, inside a live
   * scope) with its illustration still, and its title is said through the
   * app's live region. Never for an empty that loads, which is simply there.
   */
  live?: boolean;
  className?: string;
};

/**
 * Empty state in the deck's dialect (spec 07 › Estados vacíos): icon tile,
 * title in Outfit, short description, primary + ghost actions.
 */
export function EmptyState({
  icon,
  illustration,
  title,
  description,
  preserved,
  action,
  secondaryAction,
  variant = "plain",
  size = "default",
  live = false,
  className,
}: EmptyStateProps) {
  const compact = size === "compact";
  const rootRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLParagraphElement>(null);
  const announceTitle = useAnnounceRef<HTMLParagraphElement>("polite", live);
  const setTitleRef = useCallback(
    (node: HTMLParagraphElement | null) => {
      titleRef.current = node;
      announceTitle(node);
    },
    [announceTitle],
  );
  // Mounted where a list the person emptied held the focus: it takes it (P15:
  // never <body>) on its primary action, else its title, and says what it is
  // when it does not already (`live`).
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || !claimHeldFocus(root)) return;
    const title = titleRef.current;
    const actions = root.querySelector<HTMLElement>("[data-empty-actions]");
    const target = (actions ? getFocusableElements(actions)[0] : undefined) ?? title;
    if (target === title && title && !title.hasAttribute("tabindex")) title.setAttribute("tabindex", "-1");
    target?.focus({ preventScroll: true });
    if (!live && title) {
      const text = announceableText(title);
      if (text) announce(text);
    }
    // Once, as it mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={rootRef}
      className={cn(
        "flex flex-col items-center justify-center px-6 text-center",
        live && "enter-rise",
        compact ? "gap-1.5 py-10" : "gap-2 py-16",
        variant === "card" && "rounded-lg border border-border bg-card",
        variant === "dashed" && "rounded-lg border border-dashed border-border-strong",
        className,
      )}
    >
      {illustration ? (
        <div className={cn("text-muted-foreground", compact ? "mb-1" : "mb-3")} aria-hidden="true">
          {illustration}
        </div>
      ) : icon ? (
        <div
          className={cn(
            "flex items-center justify-center rounded-xl bg-muted text-muted-foreground ring-1 ring-inset ring-border [&_svg]:shrink-0",
            compact ? "mb-1 size-10 [&_svg]:size-5" : "mb-2 size-12 [&_svg]:size-6",
          )}
          aria-hidden="true"
        >
          {icon}
        </div>
      ) : null}
      <p
        ref={setTitleRef}
        className={cn(
          "text-balance font-semibold text-foreground outline-none",
          compact ? "text-body" : "font-display text-title tracking-tight",
        )}
      >
        {title}
      </p>
      {description ? (
        <p className="max-w-[44ch] text-pretty text-label text-muted-foreground">{description}</p>
      ) : null}
      {preserved ? <p className="max-w-[44ch] text-label text-subtle-foreground">{preserved}</p> : null}
      {action || secondaryAction ? (
        <div data-empty-actions="" className={cn("flex flex-wrap items-center justify-center gap-2", compact ? "mt-2" : "mt-4")}>
          {action}
          {secondaryAction}
        </div>
      ) : null}
    </div>
  );
}
